import './policyStatus.less'
import EmptyStateSvg from '../../../../assets/inlineSvg/no_data_circle.svg'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_POLICIES,
  URL_RULE_SET
} from '../../../../constants'
import { getShowLoader } from '../../../../reducers/requestReducer'
import { RootState } from '../../../../rootReducer'
import LoaderView from '../../../../components/Loader/loader'
import PolicyItem from '../components/policyItem'
import {
  fetchRulsets,
  resetPolicies,
  ACTION_POLICIES_RULE_SETS,
  FetchRuleSetParams
} from '../../../policies/policiesSlice'
import { RulsetInfo } from '../../../../interfaces'
import useUrlContext from '../../../../hooks/useUrlContext'
import { mockPolicyStatusWidgetData } from '../../renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import { Typography } from '@lightbeamai/design-system'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { CaretRight } from 'phosphor-react'
import { connect } from 'react-redux'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  policies?: RulsetInfo[]
  showLoader: boolean
}

interface DispatchProps {
  fetchRulsets: (params: FetchRuleSetParams) => void
  resetPolicies: () => void
}

type PolicyStatusComponentProps = StateProps & DispatchProps & OwnProps

const PolicyStatus = (props: PolicyStatusComponentProps) => {
  const {
    policies = [],
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDatasourceType,
    widgetDashboardId,
    fetchRulsets,
    resetPolicies
  } = props
  const intl = useIntl()
  const urlContext = useUrlContext()
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const linkPoliciesCards = getUpdatedUrl({
    pageName: URL_RULE_SET,
    pathName: URL_POLICIES + URL_RULE_SET,
    pageQueryParams: {
      [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.card
    }
  })

  const policiesGroupedByTypes = policies.reduce((groupedPolicies, policy) => {
    if (policy.type) {
      groupedPolicies[policy.type] = (groupedPolicies[policy.type] || 0) + policy.alertsCount
    }
    return groupedPolicies
  }, {})

  const listGroupedByPolicyType = Object.entries(policiesGroupedByTypes).map(([key, val]) => ({
    policyType: key,
    alertsCount: (val || 0) as number
  }))

  const listSorted = listGroupedByPolicyType
    .slice()
    .sort((a, b) => b.alertsCount - a.alertsCount)
    .filter((policy) => policy.alertsCount > 0)

  const dataSourceId = widgetDatasourceId
    ? widgetDatasourceId
    : urlContext.contextFilters[DATA_SOURCE_ID] || ''
  const datasourceType = widgetDatasourceType
    ? widgetDatasourceType
    : ((urlContext.queryParams[DATA_SOURCE_TYPE] || '') as string)

  useEffect(() => {
    if (useMockData) return
    fetchRulsets({
      [DATA_SOURCE_ID]: dataSourceId,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
    return () => resetPolicies()
  }, [fetchRulsets, resetPolicies, useMockData, dataSourceId, widgetIndex])

  return (
    <div className="lb-widget-card policy-status  xs-p-16">
      <div className="policy-status-widget" data-test-id="main-dashboard-policy-status-widgets">
        <div className="header-wrapper">
          <Typography color="primary" variant="h6">
            {intl.formatMessage({ id: 'policyStatus.widget.title' })}
          </Typography>
          <Link to={linkPoliciesCards} className="policy-link" target="_blank">
            <Typography color="primary" type="medium" variant="base">
              {intl.formatMessage({ id: 'dashboard.widget.allPolicies' })}
            </Typography>
            <CaretRight size="12" color="var(--color-purple)" weight="bold" />
          </Link>
        </div>
        <div className="policy-items xs-mt-24">
          {listSorted.slice(0, 3).map((policy, i) => (
            <PolicyItem key={i} policy={policy} datasourceType={datasourceType} />
          ))}
          {listSorted.length === 0 && (
            <div className="empty-state-icon">
              <EmptyStateSvg />
              <Typography
                className="xs-mt-12"
                variant="baseMinus2"
                type="medium"
                color="placeholder"
              >
                {intl.formatMessage({ id: 'dashboard.widget.noPolicy' })}
              </Typography>
            </div>
          )}
        </div>

        <LoaderView showLoader={showLoader} size="small" />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  policies: ownProps?.useMockData
    ? (mockPolicyStatusWidgetData.policies as RulsetInfo[])
    : state.policies.rulesets?.widgetData[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.list,
  showLoader: getShowLoader(ACTION_POLICIES_RULE_SETS)
})

const mapDispatchToProps: DispatchProps = { fetchRulsets, resetPolicies }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(PolicyStatus)
